import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";

type AuditRow = {
  id: string;
  created_at: string;
  action: string;
  student_user_id: string | null;
  parent_user_id: string | null;
  performed_by: string | null;
  status: string;
  details: any;
};

export default function InstitutionAudit() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: mem } = await (supabase as any)
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .eq("member_role", "institution_admin")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      const org = mem?.organization_id;
      if (!org) {
        setLoading(false);
        return;
      }
      const { data } = await (supabase as any)
        .from("institution_audit_log")
        .select("*")
        .eq("organization_id", org)
        .order("created_at", { ascending: false })
        .limit(500);
      setRows((data ?? []) as AuditRow[]);
      setLoading(false);
    })();
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.action.toLowerCase().includes(q) ||
        JSON.stringify(r.details ?? {}).toLowerCase().includes(q),
    );
  }, [rows, search]);

  const exportCsv = () => {
    const header = "timestamp,action,student,parent,performed_by,status,details\n";
    const body = filtered
      .map((r) =>
        [
          r.created_at,
          r.action,
          r.student_user_id ?? "",
          r.parent_user_id ?? "",
          r.performed_by ?? "",
          r.status ?? "",
          `"${JSON.stringify(r.details ?? {}).replace(/"/g, '""')}"`,
        ].join(","),
      )
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `institution-audit-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHead title="Institution Audit Log | WeKIT™" description="Compliance & activity trail" path="/institution/audit" />
      <div className="container max-w-6xl py-10 space-y-6">
        <header className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Audit log</h1>
            <p className="text-muted-foreground mt-1">Invitations, consents and access events.</p>
          </div>
          <Button variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
        </header>
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Events</CardTitle>
            <Input
              placeholder="Filter by action or detail…"
              className="max-w-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground text-sm">No events yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium">{r.action}</TableCell>
                      <TableCell className="text-xs">{r.student_user_id?.slice(0, 8) ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.parent_user_id?.slice(0, 8) ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.performed_by?.slice(0, 8) ?? "—"}</TableCell>
                      <TableCell>{r.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
